/**
 * The hub's derived data, as one hook — extracted from `DropHubSurface.tsx`
 * against the 400-line ceiling. Owns the lookup indexes, the wheel access
 * state, and the bar's standing readout; the surface consumes, never derives.
 */
import React from "react";
import type {SurfaceContentBlockResponse} from "@/hooks/hub/useGetHub";
import {contentBlockSlots} from "@/surfaces/dropSlots";
import {useGetSectionSublabels} from "@/hooks/gamification/useGetSectionSublabels";
import {useGetSurfaceWheel} from "@/hooks/gamification/useGetSurfaceWheel";
import type {MyBadges} from "@/hooks/useGetMyBadges";
import {useGetDropAchievements} from "@/hooks/drop/useGetDropAchievements";
import {useGetLedgerStandings} from "@/hooks/ledger-boards/useGetLedgerStandings";
import {useJoinLedgerBoardRoom} from "@/hooks/ledger-boards/useJoinLedgerBoardRoom";
import type {SurfaceActionBarItem} from "@/surfaces/spine/SurfaceActionBar";
import {ordinal, weekLabel, BOARD_PERIOD_WORD} from "@/surfaces/spine/dropSpineData";
import type {DropSection} from "@/hooks/drop/useGetDrop";

export function useDropHubData(input: {
    drop: any;
    enabledSections: DropSection[];
    railSections: DropSection[];
    fullToken: any;
    user?: { nickname?: string; firstName?: string; lastName?: string };
}) {
    const {drop, enabledSections, railSections, fullToken, user} = input;

    /** Slot id -> the section it came from, for the two things only it knows. */
    const sectionById = React.useMemo(
        () => new Map(enabledSections.map(sec => [
            String((sec as { _id?: string })._id),
            sec as DropSection & { type?: string; label?: string; title?: string },
        ])),
        [enabledSections],
    );

    const contentBlocks: SurfaceContentBlockResponse[] = drop.contentBlocks ?? [];

    /*
     * Keyed by the SAME rule `contentBlockSlots` derives slot ids with — the
     * serializer strips `_id`, so keying on it alone put every block under
     * "undefined" and no slot ever found its block.
     */
    const contentBlockById = React.useMemo(
        () => new Map(contentBlocks.map((b, i) => [String(b._id ?? b.id ?? `block-${i}`), b])),
        [contentBlocks],
    );

    /*
     * A LEADERBOARD content block naming the SAME board a LEDGER_BOARD
     * section already places is the section's duplicate, not a second
     * occupant — the section owns the column and the block slot is dropped.
     * (The block itself still serves the bar's standing readout.)
     */
    const liveBlockSlots = contentBlockSlots(contentBlocks).filter(slot => {
        const block = contentBlockById.get(slot.id);
        const boardId = block?.type === "LEADERBOARD"
            ? block.leaderboardConfig?.ledgerBoardId
            : undefined;
        return !boardId || !railSections.some(s =>
            s.engagementType === "LEDGER_BOARD" && s.engagementId === boardId);
    });

    /*
     * Live rail sublabels ("Ready now", "6 days") resolved from the sections'
     * gamification engagements — state, not authored copy.
     */
    const sectionSublabels = useGetSectionSublabels(drop.sections);

    /* The surface's wheel, if a section points at one. */
    const surfaceWheel = useGetSurfaceWheel(drop.sections);

    /*
     * The wheel is UNPLACED — the designed experience is the floating prompt
     * + modal, never a band. The counter re-opens it on repeated requests.
     */
    const [wheelOpenRequest, setWheelOpenRequest] = React.useState(0);
    const requestWheel = () => setWheelOpenRequest(n => n + 1);
    const isWheelSection = (s: DropSection): boolean =>
        Boolean(surfaceWheel && (s as { engagementId?: string }).engagementId === surfaceWheel.modelId);
    /*
     * No spins, no doors: a viewer with nothing to spin — signed out, or
     * today's spin used — sees no wheel access at all.
     */
    const wheelAccessible = Boolean(surfaceWheel && surfaceWheel.spinsRemaining > 0);

    /*
     * One badges read serves the bar count and the stash so they never
     * disagree — and it is the SURFACE's read, not the platform's. The global
     * NFT-ownership hook counted every virtual collectible the account ever
     * held (GoVIP POP badges, LootBoxes), so a brand-new campaign member
     * opened the hub wearing a double-digit chip. This campaign-scoped read
     * starts everyone at zero and matches the stash by construction.
     */
    const {data: achievementsData, isLoading: badgesLoading} = useGetDropAchievements(drop?.slug, fullToken);
    const myBadges: MyBadges = React.useMemo(() => ({
        badges: (achievementsData?.inventory ?? []).map(b => ({
            trackerId: b.trackerId,
            name: b.name,
            imageUrl: b.imageUrl,
            count: b.count,
            earnedAt: b.earnedAt,
        })),
        count: achievementsData?.totalOwned ?? 0,
        loading: badgesLoading,
    }), [achievementsData, badgesLoading]);

    /*
     * THE BAR'S STANDING IS SURFACE DATA, NEVER THE PLATFORM'S — the
     * surface's board supplies rank · points · its own period word. No board,
     * or no standing on it, renders NOTHING.
     */
    const boardBlock = contentBlocks
        .find(b => b.type === "LEADERBOARD" && b.leaderboardConfig?.ledgerBoardId);
    const {data: boardStanding} = useGetLedgerStandings(boardBlock?.leaderboardConfig?.ledgerBoardId, {limit: 1});
    /*
     * JOIN THE BOARD'S ROOM FROM THE SURFACE, not from the board component.
     *
     * The bar's own standing moves on the same push the board does, and a
     * member earning from the wheel or a Realm action may have any section
     * open. Joining only where the board renders meant the rank beside their
     * name went stale until a reload. Joining twice is harmless — the server
     * dedupes by room.
     */
    useJoinLedgerBoardRoom(boardBlock?.leaderboardConfig?.ledgerBoardId);
    const memberStanding = boardStanding?.myRank != null
        ? [
            ordinal(boardStanding.myRank),
            "·",
            `${(boardStanding.myPoints ?? 0).toLocaleString()}`,
            BOARD_PERIOD_WORD[boardStanding.periodType ?? ""] ?? "",
        ].join(" ").trim()
        : undefined;
    const campaignWeekLabel = boardStanding?.periodKey && drop.programName
        ? weekLabel(boardStanding.periodKey)
        : undefined;

    /*
     * THE BAR'S RIGHT-HAND RUN — what the viewer HAS, in the anchor's order:
     * the number and its period first, the placing as the trailing qualifier,
     * then a spin if one is waiting. Each item is OMITTED rather than zeroed —
     * "0 spins ready" is a line about something the viewer cannot do, and a
     * placing with no board behind it is a number we invented.
     */
    const barStanding: SurfaceActionBarItem[] = [];
    if (boardStanding?.myRank != null) {
        barStanding.push({
            id: "standing",
            label: `${(boardStanding.myPoints ?? 0).toLocaleString()} ${BOARD_PERIOD_WORD[boardStanding.periodType ?? ""] ?? ""}`.trim(),
            detail: ordinal(boardStanding.myRank),
        });
    }
    /*
     * NO SPIN COUNT HERE. The wheel already announces itself in the rail, in
     * the floating control and in the spin panel — a fourth statement in the
     * bar is repetition, not reinforcement.
     */

    const memberName = user?.nickname ||
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        undefined;

    return {
        sectionById, contentBlocks, contentBlockById, liveBlockSlots,
        sectionSublabels, surfaceWheel, wheelOpenRequest, requestWheel,
        isWheelSection, wheelAccessible, myBadges, memberStanding,
        campaignWeekLabel, memberName, barStanding,
    };
}
